import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createWidgetFromCashbot } from '../../../../app/store/slices/adminSlice';
import { createUserWidgetFromCashbot } from '../../../../app/store/slices/userDashboardSlice';
import { toast } from 'react-toastify';

const CreateWidgetModal = ({ isOpen, onClose, message, context = 'admin', canvasId = null, canvasName = '' }) => {

  const dispatch = useDispatch();
  const adminLoading = useSelector((state) => state.admin?.loading);
  const userDashboardLoading = useSelector((state) => state.userDashboard?.loading);
  const loading = context === 'admin' ? adminLoading : userDashboardLoading;
  const [isCreating, setIsCreating] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState({
    title: `${message?.widgetConfig?.title?.substring(0, 40) || 'New Widget'}`,
    type: message?.widgetType || message?.config?.widgetType,
    position: { x: 0, y: 0, w: 4, h: 3 },
    config:{
      data:message?.data,
      type:message?.widgetConfig?.visualization?.chartType || message?.widgetConfig.chartType},
      visualization: message?.widgetConfig?.visualization
  });
  const [showFullData, setShowFullData] = useState(false);
  const handleCreate = async () => {
    try {
      setIsCreating(true); 
      if (context === 'admin') {
        await dispatch(createWidgetFromCashbot({
          query: message.query,
          widgetConfig: {
            ...widgetConfig,
            data: message?.data,
            type: widgetConfig.type,
            title: widgetConfig.title,
            config: {
              ...widgetConfig.config,
              type: widgetConfig.type
            },
            visualization: message?.widgetConfig?.visualization
          }
        })).unwrap();
      } else if (context === 'user' && canvasId) {
        await dispatch(createUserWidgetFromCashbot({
          query: message.query,
          widgetConfig: {
            ...widgetConfig,
            data: message.data,
            type: widgetConfig.type,
            title: widgetConfig.title,
            config: {
              ...widgetConfig.config,
              type: widgetConfig.type
            },
            visualization: message?.widgetConfig?.visualization
          },
          canvasId: canvasId
        })).unwrap();
      } else {
        throw new Error('Missing context or canvasId for user context');
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating widget:', error);
      toast.error(`Failed to create widget: ${error.message || 'Unknown error'}`);
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;
  
  const isLoading = loading || isCreating;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">
            Create {context === 'admin' ? 'Admin' : 'User'} Widget
            {context === 'user' && canvasName && (
              <span className="text-sm text-gray-500 block">
                Canvas: {canvasName}
              </span>
            )}
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
            disabled={isLoading}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Widget Title
            </label>
            <input
              type="text"
              value={widgetConfig.title}
              onChange={(e) => setWidgetConfig({ ...widgetConfig, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Widget Type
            </label>
            <select
              value={widgetConfig.type}
              onChange={(e) => setWidgetConfig({
                ...widgetConfig,
                type: e.target.value,
                config: {
                  ...widgetConfig.config,
                  type: e.target.value
                }
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              <option value="metric">Metric</option>
              <option value="chart">Chart</option>
              <option value="table">Table</option>
              <option value="list">List</option>
            </select>
          </div>

          <div className="bg-gray-50 p-3 rounded">
            <p className="text-sm text-gray-600">
              <strong>Query:</strong> {message.query}
            </p>
           {message.data && (
              <div className="mt-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-gray-500 flex-1">
                    <strong>Data Preview:</strong>{' '}
                    {!showFullData && (() => {
                      const str = JSON.stringify(message.data);
                      return str.length > 120 ? str.substring(0, 120) + '...' : str;
                    })()}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowFullData(v => !v)}
                    className="text-sm font-bold text-btn-primary hover:underline shrink-0"
                  >
                    {showFullData ? 'Collapse' : 'Expand'}
                  </button>
                </div>
                {showFullData && (
                  <pre className="mt-1 max-h-56 overflow-auto text-[11px] leading-snug bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap">
                  {JSON.stringify(message.data, null, 2)}
                  </pre>
                )}
            </div>
            )}  
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isLoading}
            className="px-4 py-2 bg-btn-primary text-white rounded-md hover:bg-btn-primary-hover disabled:opacity-50 transition-colors flex items-center justify-center min-w-[120px]"
          >
            {isLoading ? (
              <>
                <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              'Create Widget'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateWidgetModal;